import { images, serviceVisuals, serviceVisualAlt } from './site'
import type { Service, ServiceCategory } from '../types/service'

export const categoryOrder: ServiceCategory[] = [
  'Remodeling',
  'Home Systems',
  'Exterior',
  'Commercial',
  'Specialty',
]

export const categoryBlurbs: Record<ServiceCategory, string> = {
  Remodeling:
    'Kitchens, baths, basements and additions — reworking the space you already own.',
  'Home Systems':
    'The plumbing, electrical and mechanical work behind the walls, plus the fixtures in front of them.',
  Exterior:
    'Roof to foundation: shingles, siding, gutters, windows, doors, decks, patios and concrete.',
  Commercial:
    'Repair, updates and renovation for commercial property, including code work and sign repair.',
  Specialty:
    'Water filtration, appliance hook-ups, carpentry, home theater and other focused work.',
}

export const services: Service[] = [
  {
    slug: 'kitchens',
    name: 'Kitchen Remodeling',
    category: 'Remodeling',
    summary:
      'Budget-friendly makeovers through pro-style kitchens with stainless appliances.',
    image: serviceVisuals.kitchen,
    imageAlt: serviceVisualAlt.kitchen,
    intro: [
      'The kitchen is where a home gets used hardest, and it is one of the strongest places to put a remodeling dollar. We handle everything from a budget-friendly makeover of the kitchen you already have, up to a pro-style kitchen with stainless appliances.',
      'We start with design planning so the layout works before anything is torn out — modern or traditional, whichever suits the house.',
    ],
    blocks: [
      {
        title: 'What a kitchen project covers',
        bullets: [
          'Design planning for modern or traditional kitchens',
          'Improved traffic flow through the room',
          'Clearly defined work areas',
          'Increased storage',
          'Cabinets, countertops and hardware',
          'Flooring, tile and lighting',
          'Kitchen plumbing and electrical',
          'Appliance installation and hook-up',
        ],
      },
      {
        title: 'Value in the home',
        body: 'Kitchen remodeling is one of the improvements homeowners make specifically to add value to the property, not only to make the room more comfortable to live in.',
      },
    ],
  },
  {
    slug: 'bathrooms',
    name: 'Bathroom Remodeling',
    category: 'Remodeling',
    summary:
      'Full bathroom renovation, redesign and finish work — tile, glass, cabinets and fixtures.',
    image: serviceVisuals.bathroom,
    imageAlt: serviceVisualAlt.bathroom,
    intro: [
      'Bathroom renovation, remodeling and redesign, handled from the rough plumbing through the last piece of cabinet hardware.',
      'Whether the goal is a tighter, better-organized family bath or a spa-style master, we plan the room around how it actually gets used.',
    ],
    blocks: [
      {
        title: 'Surfaces and finishes',
        bullets: [
          'Flooring and walls',
          'Tile work',
          'Carpeting',
          'Custom glass enclosures for showers and tubs',
          'Mirrors',
        ],
      },
      {
        title: 'Cabinetry and countertops',
        bullets: [
          'Hardwood vanities and cabinets',
          'Custom countertops',
          'Granite or marble countertops',
          'Cabinet hardware',
          'Organization and storage planning',
        ],
      },
      {
        title: 'Behind the walls',
        bullets: [
          'Bathroom plumbing',
          'Electrical',
          'Fixtures',
          'Spa-style design planning',
        ],
      },
    ],
  },
  {
    slug: 'basements',
    name: 'Basement Finishing',
    category: 'Remodeling',
    summary:
      'Turn underused basement space into dry, warm family living space — without losing storage.',
    image: serviceVisuals.basement,
    imageAlt: serviceVisualAlt.basement,
    intro: [
      'A basement is usually the largest block of underused square footage in the house. Finishing it converts that space into valuable living space while keeping the storage and utility access you still need.',
      'We also remodel older finished basements that have dated out or taken on water.',
    ],
    blocks: [
      {
        title: 'What we build downstairs',
        bullets: [
          'Basement finishing and remodeling',
          'Dry, warm family living space',
          'Storage and utility areas kept accessible',
          'Framing, drywall and flooring',
          'Basement electrical and lighting',
          'Basement plumbing and added bathrooms',
          'Remodeling of older finished basements',
        ],
      },
    ],
  },
  {
    slug: 'additions-new-construction',
    name: 'Additions & New Construction',
    category: 'Remodeling',
    summary:
      'Room additions, second stories, dormers and garages from a full-service licensed builder.',
    image: serviceVisuals.additions,
    imageAlt: serviceVisualAlt.additions,
    intro: [
      'As a full service, licensed and insured builder, we handle new construction and structural additions — not just surface remodeling.',
      'That means we can take a project from design and permitting through framing, systems, finish work and clean-up.',
    ],
    blocks: [
      {
        title: 'Building services',
        bullets: [
          'New construction',
          'Remodeling and renovation',
          'New room additions',
          'Second story additions',
          'Dormers',
          'Master bedroom additions',
          'Master bathroom additions',
          'Living room and family room additions',
          'Attached garages',
          'Free-standing garages',
          'Garage construction',
          'Other home building construction and improvements',
        ],
      },
    ],
  },
  {
    slug: 'plumbing',
    name: 'Plumbing',
    category: 'Home Systems',
    summary:
      'Emergency plumbing, sewer and drain cleaning, fixtures, water heaters and full inspections.',
    image: serviceVisuals.plumbing,
    imageAlt: serviceVisualAlt.plumbing,
    usePlumbingPhone: true,
    intro: [
      'Leaky faucets, running toilet or frozen pipes? Our plumbing line is 248-761-7598.',
      'We cover emergency plumbing service and everyday plumbing maintenance, repair, replacement and installation throughout the house.',
    ],
    blocks: [
      {
        title: 'Emergency and urgent work',
        bullets: [
          'Emergency plumbing service',
          'Sewer and drain cleaning',
          'Leaky faucets',
          'Running toilets',
          'Frozen pipes',
          'Burst pipes',
          'Flooding basements',
        ],
      },
      {
        title: 'Fixtures and connections',
        bullets: [
          'Plumbing fixtures',
          'Lavatories and sinks',
          'Water closets with flushing devices',
          'Valves and leaking connections',
          'Garbage disposals',
          'Drinking water faucets',
          'Refrigerator water hookups',
          'Dishwasher installation, seals and leak repair',
        ],
      },
      {
        title: 'Pipes, heaters and water treatment',
        bullets: [
          'Pipe repair and replacement',
          'Copper pipe and PEX',
          'Hot water tanks',
          'Tankless / on-demand water heaters',
          'Water softeners',
          'Whole-house water filter systems',
        ],
      },
      {
        title: 'Rooms and inspections',
        bullets: [
          'Bathroom plumbing',
          'Kitchen plumbing',
          'Plumbing maintenance and repair',
          'Full plumbing inspections',
          'Repair, replacement and new installations',
        ],
      },
    ],
  },
  {
    slug: 'electrical',
    name: 'Electrical & Lighting',
    category: 'Home Systems',
    summary:
      'From rewiring a whole house to changing a light bulb, plus lighting and ballast replacement.',
    image: serviceVisuals.electrical,
    imageAlt: serviceVisualAlt.electrical,
    intro: [
      'From rewiring a whole house to changing a light bulb, we provide complete electric services.',
      'That includes the electrical package for new builds, additions, renovations and remodels, as well as small fixture and lighting calls.',
    ],
    blocks: [
      {
        title: 'Electrical services',
        bullets: [
          'Whole-house rewiring',
          'Complete electric for new builds',
          'Electrical for additions, renovations and remodels',
          'Lighting installation',
          'Ballast replacement',
          'Fixture and bulb changes',
        ],
      },
    ],
  },
  {
    slug: 'hvac',
    name: 'Heating & Cooling',
    category: 'Home Systems',
    summary:
      'Furnace and air conditioner repair, replacement and installation, plus duct work.',
    intro: [
      'Heating and cooling work is available alongside our remodeling and construction services, including the duct work that goes with an addition or a finished basement.',
    ],
    blocks: [
      {
        title: 'HVAC services',
        bullets: [
          'Furnace repair, replacement and installation',
          'Air conditioner repair, replacement and installation',
          'Duct cleaning services',
          'Custom duct fabrication',
        ],
      },
    ],
  },
  {
    slug: 'water-filtration',
    name: 'Drinking Water Filtration',
    category: 'Specialty',
    summary:
      'eSpring Water Purifier installation — UV light plus a patented carbon-block filter.',
    intro: [
      'We install drinking water filtration, including the eSpring Water Purifier, which combines ultraviolet light with a patented carbon-block filter.',
      'It is certified to reduce more than 140 potential health-effect contaminants.',
    ],
    blocks: [
      {
        title: 'Certifications',
        bullets: [
          'Water Quality Association Gold Seal',
          'NSF/ANSI Standard 42',
          'NSF/ANSI Standard 53',
          'NSF/ANSI Standard 55B',
        ],
      },
      {
        title: 'Installation options',
        bullets: [
          'Countertop',
          'Below-counter',
          'Refrigerator connection',
          'Icemaker connection',
        ],
      },
      {
        title: 'Maintenance',
        body: 'The cartridge is replaced every year or every 1,320 gallons, whichever comes first. We also install water softeners and whole-house water filter systems as part of our plumbing work.',
      },
    ],
  },
  {
    slug: 'appliance-hook-ups',
    name: 'Appliance Hook-Ups',
    category: 'Specialty',
    summary:
      'Help from purchase through installation, delivery of new units and removal of the old.',
    image: images.appliances,
    intro: [
      'We help from purchase all the way through installation. We can deliver your new appliances and take the old ones away.',
      'If you are shopping, we can point you toward Energy Star appliances and the rebate programs that may apply to them.',
    ],
    blocks: [
      {
        title: 'What we handle',
        bullets: [
          'Guidance from purchase to installation',
          'Delivery of new appliances',
          'Removal and haul-away of old appliances',
          'Water, gas and electrical connections',
          'Dishwasher installation, seals and leak repair',
          'Refrigerator water hookups',
          'General information on Energy Star appliances and rebates',
        ],
      },
    ],
  },
  {
    slug: 'roofing-siding-gutters',
    name: 'Roofing, Siding & Gutters',
    category: 'Exterior',
    summary:
      'Roof repair and replacement, re-shingling, siding in aluminum, vinyl or wood, and gutters.',
    image: serviceVisuals.roofing,
    imageAlt: serviceVisualAlt.roofing,
    intro: [
      'The exterior shell is what keeps everything else dry. We repair and replace roofs, install and repair siding, and update gutters.',
      'We will walk you through roofing material options and what a tear-off involves before the work starts.',
    ],
    blocks: [
      {
        title: 'Roofing',
        bullets: [
          'Roof repair and replacement',
          'Re-shingling',
          'Asphalt roofs',
          'Slate roofs',
          'Metal roofs',
          'Shingle tear-offs',
          'Roofing material guidance',
          'Roof energy-efficiency framing',
        ],
      },
      {
        title: 'Siding and gutters',
        bullets: [
          'Siding repairs and installation',
          'Aluminum, vinyl and wood siding',
          'Gutter updates and repairs',
          'Gutter work to help protect foundation integrity',
        ],
      },
    ],
  },
  {
    slug: 'decks-patios',
    name: 'Decks & Patios',
    category: 'Exterior',
    summary:
      'Deck and patio design, construction and repair, plus stamped and stained concrete patios.',
    image: serviceVisuals.decks,
    imageAlt: serviceVisualAlt.decks,
    intro: [
      'We design, build and repair decks and patios — new builds as well as repairs to the deck you already have.',
      'Planning covers how you get to the space, where seating goes, what you will be looking at, and how the railing should be handled.',
    ],
    blocks: [
      {
        title: 'Materials',
        bullets: [
          'Wood and treated wood',
          'Cedar',
          'PVC',
          'Composite decking',
          'Railings, metal posts and glass panels',
        ],
      },
      {
        title: 'Patios',
        bullets: [
          'Patio design and construction',
          'Concrete patio stamping',
          'Concrete staining and coloring',
          'Access, seating and viewing area planning',
        ],
      },
    ],
  },
  {
    slug: 'concrete',
    name: 'Concrete & Cement',
    category: 'Exterior',
    summary: 'Cement driveways and decorative stamped patios and driveways.',
    intro: [
      'Flatwork rounds out the exterior of a property. We pour cement driveways and install decorative cement where you want the surface to be part of the design.',
    ],
    blocks: [
      {
        title: 'Concrete work',
        bullets: [
          'Cement driveways',
          'Decorative cement patios',
          'Stamped cement patios and driveways',
          'Staining and coloring',
        ],
      },
    ],
  },
  {
    slug: 'doors-windows',
    name: 'Doors & Windows',
    category: 'Exterior',
    summary:
      'Replacement windows and entry door replacement and installation.',
    intro: [
      'Windows and entry doors get judged on three things at once: how much energy they lose, how secure they are, and whether they suit the house. We work through all three before you order.',
    ],
    blocks: [
      {
        title: 'Doors and windows',
        bullets: [
          'Replacement windows',
          'Entry door replacement',
          'Entry door installation',
          'Energy efficiency',
          'Security',
          'Style and design fit',
        ],
      },
    ],
  },
  {
    slug: 'painting',
    name: 'Painting & Staining',
    category: 'Exterior',
    summary:
      'Interior and exterior painting, with prep, finish work and color and brand guidance.',
    intro: [
      'Interior and exterior painting, including the wall prep and finish work that decides how the job looks two years later.',
      'We will advise on color and on brand selection rather than leaving you to guess at the paint counter.',
    ],
    blocks: [
      {
        title: 'Interior',
        bullets: [
          'Kitchens and bathrooms',
          'Living rooms and family rooms',
          'Bedrooms',
          'Basements',
          'Wall prep and finish work',
        ],
      },
      {
        title: 'Exterior',
        bullets: [
          'Exterior homes',
          'Garages, buildings and barns',
          'Sheds',
          'Decks and fences',
        ],
      },
      {
        title: 'Products',
        bullets: [
          'Translucent stains',
          'Semi-solid stains',
          'Solid stains',
          'Paints',
          'Color and brand selection advice',
        ],
      },
    ],
  },
  {
    slug: 'flooring',
    name: 'Flooring',
    category: 'Remodeling',
    summary: 'Flooring repair, replacement and installation.',
    intro: [
      'Flooring repair, replacement and installation for a single room or a whole house, coordinated with the rest of your project so the transitions land correctly.',
    ],
    blocks: [
      {
        title: 'Flooring services',
        bullets: [
          'Carpeting',
          'Custom tiling',
          'Hardwood floors',
          'Flooring repair',
          'Flooring replacement and installation',
        ],
      },
    ],
  },
  {
    slug: 'carpentry',
    name: 'Carpentry & Handyman',
    category: 'Specialty',
    summary:
      'General carpentry, mantel and fireplace work, drywall and honey-do projects.',
    intro: [
      'Not every job is a full remodel. We take on general carpentry, home updates and the honey-do list that keeps getting pushed to next weekend.',
    ],
    blocks: [
      {
        title: 'Carpentry and small jobs',
        bullets: [
          'Mantel and fireplace-related carpentry',
          'General carpentry',
          'Drywall',
          'Home updates',
          'Handyman and honey-do style projects',
        ],
      },
    ],
  },
  {
    slug: 'home-theater',
    name: 'Home Theater, Audio & Video',
    category: 'Specialty',
    summary: 'Home theater design, construction and installation.',
    image: images.homeTheater,
    intro: [
      'Home theater design, construction and installation — the room build and the equipment install handled together. We also work on car audio and sound.',
    ],
    blocks: [
      {
        title: 'Home theater',
        bullets: [
          'Home theater design',
          'Home theater construction',
          'Home theater installation',
          'Audio and video',
          'Car audio and sound',
        ],
      },
    ],
  },
  {
    slug: 'commercial',
    name: 'Commercial Property Services',
    category: 'Commercial',
    summary:
      'Repair, updates, renovation, maintenance and lease-hold improvements.',
    image: images.servicesTag,
    intro: [
      'We work on commercial property as well as homes — ongoing maintenance, tenant work, and the updates that keep a space rentable and compliant.',
    ],
    blocks: [
      {
        title: 'Commercial services',
        bullets: [
          'Commercial property repair',
          'Commercial updates and renovation',
          'Commercial maintenance',
          'Lease-hold improvements',
          'Bringing property up to code',
        ],
      },
    ],
  },
  {
    slug: 'sign-repair',
    name: 'Commercial Sign Repair',
    category: 'Commercial',
    summary: 'Sign repair, particularly signs that are no longer lighting up.',
    image: images.signRepair,
    intro: [
      'A sign that is not lighting up is a storefront that is closed after dark. We repair commercial signs, including the electrical and ballast side of the problem.',
    ],
    blocks: [
      {
        title: 'Sign repair',
        bullets: [
          'Commercial sign repair',
          'Signs that are not lighting up',
          'Ballast replacement',
          'Lighting installation',
        ],
      },
    ],
  },
  {
    slug: 'code-violation',
    name: 'Code Violation Work',
    category: 'Commercial',
    summary: 'Commercial property work to bring a space up to code.',
    intro: [
      'If an inspection has flagged items on a commercial property, we can do the work needed to bring the space up to code.',
      'We review what has been cited, scope the work, and complete the repairs and updates required.',
    ],
    blocks: [
      {
        title: 'Scope',
        bullets: [
          'Commercial property work to bring spaces up to code',
          'Electrical, plumbing and structural repairs',
          'Coordination with the rest of your commercial maintenance',
        ],
      },
    ],
  },
]

export const getService = (slug?: string) =>
  services.find((s) => s.slug === slug)

export const servicesByCategory = (category: ServiceCategory) =>
  services.filter((s) => s.category === category)
